import { type ActivityOf, getItem } from '@wyn/content';
import { createRng } from '../rng.ts';
import { DT, type RealtimeSim, TICK_HZ } from './realtime.ts';
import { clamp, type Fx, type MinigameResult } from './types.ts';

/** Tension band that tires the fish, as percent of the gauge. */
export const FISHING_BAND = [30, 75] as const;
const PAUSE_TICKS = Math.round(0.5 * TICK_HZ);
/** A whopper's quality score is raised by this much: about one grade. */
export const WHOPPER_BONUS = 0.2;
const MAX_SECONDS = 180;

export interface FishingSim extends RealtimeSim {
  phase: 'bite' | 'pause' | 'reel';
  /** The float, during the bite: waiting, a nibble, or the true bite. */
  float: 'wait' | 'nib' | 'bite';
  /** Line tension, 0–100. */
  tension: number;
  /** How tired the fish is, 0–1. */
  tired: number;
}

/**
 * Millrace Wharf. Wait out the nibbles and strike on the true bite (press), then hold to reel and
 * keep line tension in the band until the fish tires. Ported from the Harrowmere prototype.
 */
export function fishingSim(a: ActivityOf<'fishing'>, level: number, seed: string): FishingSim {
  const rng = createRng(seed);
  const rand = (lo: number, hi: number) => lo + rng.next() * (hi - lo);
  const fish = getItem(a.catch).name;
  const window = clamp(0.75 - a.fight * 0.15 + level * 0.005, 0.35, 0.8);

  let prev = 0;
  let timer = 0;
  let next = rand(1.5, 4);
  let nibbles = rng.int(0, 3);
  let since = 0;
  let misses = 0;
  let hookQ = 0;
  let pauseLeft = 0;
  // Reel.
  let t = 0, pull = 0, nextPull = 0, inBand = 0, total = 0, snapT = 0, slackT = 0;

  const fx = (text: string, tone: Fx['tone'], cue?: string) => sim.fx.push({ tick: sim.tick, text, tone, cue });
  const end = (result: MinigameResult) => void (sim.result = result);
  const recast = () => {
    misses++;
    if (misses >= 2) {
      end({ score: 0, title: 'An empty creel', text: 'The fish outwit you today.', breakdown: [['The strike', 0]], yield: null, coin: 0, xpMult: 1 });
      return;
    }
    sim.float = 'wait';
    timer = 0;
    next = rand(2, 4.5);
    nibbles = rng.int(0, 2);
  };

  const sim: FishingSim = {
    tick: 0,
    result: null,
    fx: [],
    phase: 'bite',
    float: 'wait',
    tension: 50,
    tired: 0,
    step(mask) {
      if (sim.result) return;
      const held = (mask & 1) === 1;
      const pressed = held && !prev;
      prev = mask & 1;
      sim.tick++;

      if (sim.phase === 'bite') {
        if (pressed) {
          if (sim.float === 'bite') {
            hookQ = clamp(1 - (since / window) * 0.6, 0.3, 1);
            fx('Hooked!', 'good', 'hooked');
            sim.phase = 'pause';
            pauseLeft = PAUSE_TICKS;
            return;
          }
          fx('Too soon, it spooks', 'bad', 'spooked');
          recast();
          return;
        }
        timer += DT;
        if (sim.float === 'wait') {
          if (timer >= next) {
            timer = 0;
            if (nibbles > 0) {
              nibbles--;
              sim.float = 'nib';
            } else {
              sim.float = 'bite';
              since = 0;
            }
          }
        } else if (sim.float === 'nib') {
          if (timer > 0.45) {
            sim.float = 'wait';
            timer = 0;
            next = rand(0.6, 1.8);
          }
        } else {
          since += DT;
          if (since > window) {
            fx('It got away', 'bad', 'escaped');
            recast();
          }
        }
      } else if (sim.phase === 'pause') {
        if (--pauseLeft <= 0) sim.phase = 'reel';
      } else {
        t += DT;
        if (t >= nextPull) {
          pull = rng.chance(0.25) ? a.fight * rand(20, 32) : rand(-1, 1) * a.fight * 22;
          nextPull = t + rand(0.4, 1.1);
        }
        sim.tension = clamp(sim.tension + (held ? 50 : -42) * DT + pull * DT, 0, 100);
        total += DT;
        const [lo, hi] = FISHING_BAND;
        if (sim.tension >= lo && sim.tension <= hi) {
          sim.tired += DT / (5 + a.fight * 1.5);
          inBand += DT;
        } else if (sim.tension < lo) sim.tired = Math.max(0, sim.tired - DT / 10);

        if (sim.tension >= 99) snapT += DT;
        else snapT = 0;
        if (sim.tension < lo) slackT += DT;
        else slackT = 0;

        const lost = snapT > 0.35 ? 'snap' : slackT > 2.5 ? 'slack' : null;
        if (lost) {
          fx(lost === 'snap' ? 'SNAP!' : 'It slips the hook', 'bad', lost);
          end({
            score: hookQ * 0.2,
            title: lost === 'snap' ? 'The line snaps' : 'It slips the hook',
            text: `The ${fish.toLowerCase()} escapes back into the depths.`,
            breakdown: [['The strike', hookQ], ['The fight', 0]],
            yield: null,
            coin: 0,
            xpMult: 1,
            feats: lost === 'snap' ? ['line-snapped'] : [],
          });
          return;
        }
        if (sim.tired >= 1) {
          const q = inBand / total;
          const whopper = rng.next() > 0.8;
          fx('Landed!', 'good', 'landed');
          end({
            score: hookQ * 0.3 + q * 0.7,
            title: whopper ? `A whopper of a ${fish.toLowerCase()}` : `You land a ${fish.toLowerCase()}`,
            text: whopper ? 'The wharf hands gather round to admire it. It will fetch a better price than most.' : 'Into the creel it goes.',
            breakdown: [['The strike', hookQ], ['The fight', q]],
            yield: { itemKey: a.catch, quantity: 1, graded: true, ...(whopper ? { bonus: WHOPPER_BONUS } : {}) },
            coin: 0,
            xpMult: 1,
            feats: whopper ? ['whopper'] : [],
          });
          return;
        }
      }
      if (sim.tick >= MAX_SECONDS * TICK_HZ)
        end({ score: 0, title: 'The light fails', text: 'You pack up your rod as dusk falls.', breakdown: [], yield: null, coin: 0, xpMult: 1 });
    },
  };
  return sim;
}
